import React, {useCallback, Dispatch} from "react";
import {useForm} from "react-hook-form";
import {uploadSingleFile} from "../api/utils";
import ChannelDetails from "./ChannelDetails";
import {useDispatch} from "./store";
import {ChannelSettingsState, ChannelSettingsAction} from "./ChannelSettings";
import {confirm} from "../components/Confirm";
import {alert} from "../components/Alert";
import {removeGroupPicture, userContainsAccountManagerRoles} from "./utils";
import {showErrorToast} from "../Utilities";

type Props = {
    cancelModal: () => void;
    state: ChannelSettingsState;
    dispatch: Dispatch<ChannelSettingsAction>;
};

function ChannelSettingsModal(props: Props) {
    const {state, cancelModal, dispatch} = props;
    const {conversation, permissions, roles} = state;
    const chatDispatch = useDispatch();
    const {register, handleSubmit} = useForm({
        mode: "onBlur",
        defaultValues: {
            channelName: state.channelName,
            channelDescription: state.description,
            permissions: permissions,
        }
    });

    const onSubmit = async data => {
        dispatch({
            type: "SET_CHANNEL_INFORMATION",
            channelName: data.channelName,
            description: data.channelDescription,
            image: data.channelImage
        });
        if (document.querySelectorAll("[data-tab='tab']")) {
            // @ts-ignore
            document.querySelectorAll("[data-tab='tab']")[1].click();
        }
    };

    const onUpdate = async data => {
        try {
            if (conversation) {
                // @ts-ignore
                if (data.channelName !== conversation.friendlyName) {
                    // @ts-ignore
                    await conversation.updateFriendlyName(data.channelName).catch(e => {
                        showErrorToast(e, 'Error updating conversation name.').then();
                    });
                }
                let update = false;
                let attributes = conversation.attributes;
                // @ts-ignore
                if (attributes["description"] !== data.channelDescription) {
                    // @ts-ignore
                    attributes["description"] = data.channelDescription;
                    update = true;
                }
                if (data.channelImage.length > 0) {
                    // @ts-ignore
                    attributes["image"] = await uploadSingleFile(data.channelImage);
                    update = true;
                }
                if (data.permissions && Array.isArray(data.permissions)) {
                    // @ts-ignore
                    attributes['permissions'] = data.permissions.map(e => parseInt(e, 10));
                    update = true;
                }
                if (update) {
                    // @ts-ignore
                    await conversation.updateAttributes(attributes);
                    removeGroupPicture();
                }
            }
        } catch (e) {
            await showErrorToast(e, 'Error updating conversation information.');
        }
        dispatch({type: "RESET"});
        cancelModal();
    };

    const archiveChannel = useCallback(async () => {
        if (conversation) {
            // @ts-ignore
            if (conversation.attributes["type"] !== "Archive") {
                if (userContainsAccountManagerRoles()) {
                    const confirmation = await confirm('Are you sure you want to archive this Group Channel?');
                    if (!confirmation) {
                        return;
                    }
                    chatDispatch({type: "ARCHIVE_CHANNEL", channel: conversation});
                    // @ts-ignore
                    let attributes = Object.assign(conversation.attributes);
                    // @ts-ignore
                    attributes["type"] = "Archive";
                    await conversation.updateAttributes(attributes);
                    dispatch({type: "SET_CHANNEL", conversation: conversation});
                    cancelModal();
                    return;
                }
                await alert('You are not allowed to archive this Group Channel.');
                // @ts-ignore
            } else if (conversation.attributes["type"] === "Archive") {
                // @ts-ignore
                let attributes = Object.assign(conversation.attributes);
                // @ts-ignore
                attributes["type"] = conversation.attributes["kind"];
                await conversation.updateAttributes(attributes);
                dispatch({type: "SET_CHANNEL", conversation: conversation});
                chatDispatch({type: "UN_ARCHIVE_CHANNEL", channel: conversation});
                cancelModal();
            }
        }
    }, [conversation, chatDispatch, cancelModal]);

    const pinChannel = useCallback(async () => {
        if (conversation) {
            // @ts-ignore
            if (conversation.attributes["type"] !== "Pin") {
                chatDispatch({type: "PIN_CHANNEL", channel: conversation});
                // @ts-ignore
                let attributes = Object.assign(conversation.attributes);
                // @ts-ignore
                attributes["type"] = "Pin";
                await conversation.updateAttributes(attributes);
                cancelModal();
                // @ts-ignore
            } else if (conversation.attributes["type"] === "Pin") {
                // @ts-ignore
                let attributes = Object.assign(conversation.attributes);
                // @ts-ignore
                attributes["type"] = conversation.attributes["kind"];
                await conversation.updateAttributes(attributes);
                chatDispatch({type: "UN_PIN_CHANNEL", channel: conversation});
                cancelModal();
            }
        }
    }, [conversation, chatDispatch, cancelModal]);

    return (
        <ChannelDetails
            errorMessage={state.errorMessage}
            inputRef={register({required: "You need to provide a conversation name", maxLength: 256})}
            pictureUploaderRef={register({required: false})}
            descriptionRef={register({required: false})}
            showNextCallback={handleSubmit(onSubmit)}
            image={state.image}
            updateNow={true}
            update={handleSubmit(onUpdate)}
            showNext={state.kindOfChannel === "Group" || state.kindOfChannel === "Pin"}
            singleChannelName={state.channelName}
            channelKind={state.kindOfChannel}
            // @ts-ignore
            isArchive={!(conversation && conversation.attributes['type'] === "Archive")}
            isArchiveCallback={archiveChannel}
            // @ts-ignore
            isPin={!(conversation && conversation.attributes['type'] === "Pin")}
            isPinCallback={pinChannel}
            numberOfButtons={state.kindOfChannel === "Duo" ? 2 : 4}
            // @ts-ignore
            kind={conversation ? conversation.attributes['kind'] : 'Group'}
            dropdownRef={register}
            roles={roles.map(e => ({name: e.roleName, value: e.id}))}
            permissions={permissions}
        />
    );
}

export default ChannelSettingsModal;
